import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import type { IncidentBand } from '../api/types';
import type { ChartAnnotation, ChartSeries } from '../components/Chart';
import {
  type QuickRange,
  REFRESH_OPTIONS,
  type TimeRange,
  fromParams,
  queryFor,
  resolve,
  sameRange,
  shift,
  writeParams,
  zoomOut,
  zoomTo,
} from './timeRange';
import { readParams, writeUrl } from './urlState';
import { useLoad } from './useLoad';

export interface PanelMeta {
  key: string;
  title: string;
  help: string;
  unit: string;
  query?: string;
  /** 'sphere-watch' panels are read from its own run history and need no Prometheus. */
  source?: 'prometheus' | 'sphere-watch';
}
export interface ChartsMeta {
  available: boolean;
  grafanaUrl: string | null;
  quickRanges: QuickRange[];
  defaultRange?: string;
  maxSpanSeconds?: number;
  panels: PanelMeta[];
  /**
   * Whether anything is collecting THIS process's /metrics. Every watch_* panel
   * exists in Prometheus only if it is — see scrapeHealth() in core/metrics.js.
   */
  selfScrape?: { ok: boolean; problem: string | null; lastAt: string | null; lastFrom: string | null } | null;
}
export interface PanelData {
  panel: { title: string; help: string; unit: string };
  series: ChartSeries[];
  unavailable?: string;
  from?: number;
  to?: number;
  step?: number;
}

const DEFAULT_MAX_SPAN_S = 30 * 86_400;
const MAX_BANDS = 120;

/**
 * Everything a screen needs to draw a set of charts over one shared window.
 *
 * Lifted out of the Charts screen when a second screen wanted the same
 * behaviour, and MOVED rather than reimplemented — the sequential loader below
 * is a decision, not an accident, and a per-screen copy would drift from it.
 *
 * THE CALLER PASSES THE KEYS IT DRAWS, and that is the point of the seam. The
 * original loaded `meta.panels` — every panel the server offers — which was
 * harmless only while one screen drew all of them. The moment panels are split
 * across screens, that shape has each screen quietly fetching the other's: range
 * queries against a shared Prometheus, on every window change, for charts it
 * renders nowhere. `scripts/verify/charts.js` asserts against exactly that.
 *
 * `keys` must be stable across renders — a module constant, not an inline array
 * — or the effect below refires forever. The dependency is its join, so a caller
 * that computes keys can memoise and still be correct.
 */
export const useChartBoard = (keys: string[]) => {
  const { data: meta, error } = useLoad(() => api.get<ChartsMeta>('/charts'), []);

  const [range, setRange] = useState<TimeRange | null>(null);
  const [refreshKey, setRefreshKey] = useState(() => {
    const wanted = readParams().get('refresh');
    return REFRESH_OPTIONS.some((option) => option.key === wanted) ? (wanted as string) : 'off';
  });
  const [tick, setTick] = useState(0);
  const [panels, setPanels] = useState<Record<string, PanelData>>({});
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  const [showIncidents, setShowIncidents] = useState(true);
  const [incidents, setIncidents] = useState<IncidentBand[]>([]);
  const lastZoom = useRef<string>('');
  const wanted = keys.join(',');

  // The URL is the state: read it once the presets are known, write it on
  // every change, so a zoomed window pasted into a chat opens on that window.
  useEffect(() => {
    if (!meta || range) return;
    setRange(fromParams(readParams(), meta.quickRanges, meta.defaultRange));
  }, [meta, range]);

  const changeRange = useCallback((next: TimeRange) => {
    setRange((current) => (current && sameRange(current, next) ? current : next));
    writeUrl(writeParams(readParams(), next));
  }, []);

  const changeRefresh = useCallback((key: string) => {
    setRefreshKey(key);
    const params = readParams();
    if (key === 'off') params.delete('refresh');
    else params.set('refresh', key);
    writeUrl(params);
  }, []);

  const maxSpan = meta?.maxSpanSeconds ?? DEFAULT_MAX_SPAN_S;

  // Every panel is asked for the SAME absolute window, resolved once per run.
  // Asking each for "the last 6h" would give sixteen windows a few seconds
  // apart, and the shared crosshair would line up on none of them.
  const win = useMemo(() => (range ? resolve(range, nowSec) : null), [range, nowSec]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `tick` is the refresh: bumping it re-reads every panel for the same window
  useEffect(() => {
    if (!meta || !range) return;
    let cancelled = false;
    setRefreshing(true);
    const at = Math.floor(Date.now() / 1000);
    setNowSec(at);
    const current = resolve(range, at);
    (async () => {
      const next: Record<string, PanelData> = {};
      const known = new Map(meta.panels.map((panel) => [panel.key, panel]));
      // Sequential rather than a burst: these are range queries against a shared
      // Prometheus that also serves Grafana and the alerting rules, and a screen
      // that fires sixteen at once every time someone changes the range is a
      // self-inflicted load spike on the thing everyone else is using.
      for (const key of wanted.split(',').filter(Boolean)) {
        if (cancelled) return;
        const panel = known.get(key);
        if (!panel) continue;
        try {
          next[key] = await api.get<PanelData>(`/charts/${key}?${queryFor(current)}`);
        } catch (caught) {
          next[key] = { panel, series: [], unavailable: (caught as Error).message || 'could not load' };
        }
        // Old windows stay dimmed on the cards this run has not reached yet, and
        // are replaced one by one rather than all at once at the end.
        if (!cancelled) setPanels((previous) => ({ ...previous, ...next }));
      }
      if (!cancelled) {
        setRefreshing(false);
        setUpdatedAt(Date.now());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meta, range, tick, wanted]);

  // The incidents sphere-watch had open during the window, as bands. Read once
  // per window; the Now screen is where they are worked, this is where they are
  // seen against the numbers. Without the Now screen's permission the server
  // sends only what a band needs (IncidentBand), so nothing here may read more.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `tick` is the refresh: bumping it re-reads the incident bands for the same window
  useEffect(() => {
    if (!range) return;
    let cancelled = false;
    api
      .get<{ incidents: IncidentBand[] }>('/incidents?status=all&limit=500')
      .then((payload) => {
        if (!cancelled) setIncidents(payload.incidents ?? []);
      })
      .catch(() => {
        if (!cancelled) setIncidents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [range, tick]);

  // Auto-refresh only makes sense for a window that follows now; on a fixed
  // window it would re-read the same seconds, so the tick still fires (a fresh
  // read is harmless) but the caption says what it is doing.
  useEffect(() => {
    const option = REFRESH_OPTIONS.find((entry) => entry.key === refreshKey);
    if (!option?.ms) return;
    const timer = setInterval(() => setTick((value) => value + 1), option.ms);
    return () => clearInterval(timer);
  }, [refreshKey]);

  // A drag on one chart arrives from every synced chart with the same window;
  // the first one wins and the rest are the same request.
  const onZoom = useCallback(
    (from: number, to: number) => {
      const next = zoomTo(from, to);
      if (!next) return;
      const key = `${next.from}-${next.to}`;
      if (key === lastZoom.current) return;
      lastZoom.current = key;
      changeRange(next);
    },
    [changeRange],
  );

  const onZoomOut = useCallback(() => {
    if (!range) return;
    changeRange(zoomOut(range, Math.floor(Date.now() / 1000), maxSpan));
  }, [range, maxSpan, changeRange]);

  const onShift = useCallback(
    (direction: -1 | 1) => {
      if (!range) return;
      changeRange(shift(range, direction, Math.floor(Date.now() / 1000)));
    },
    [range, changeRange],
  );

  const annotations = useMemo<ChartAnnotation[]>(() => {
    if (!showIncidents || !win) return [];
    const rank = { critical: 0, high: 1, warning: 2, info: 3 };
    return incidents
      .map((incident) => ({
        from: Math.floor(Date.parse(incident.firstSeenAt) / 1000),
        to:
          incident.status === 'resolved'
            ? Math.floor(Date.parse(incident.lastSeenAt) / 1000)
            : Math.max(win.to, Math.floor(Date.parse(incident.lastSeenAt) / 1000)),
        severity: incident.severity,
        title: `${incident.title}${incident.tenantKey ? ` · ${incident.tenantKey}` : ''} (${incident.status})`,
      }))
      .filter((band) => Number.isFinite(band.from) && band.to >= win.from && band.from <= win.to)
      .sort((a, b) => rank[a.severity] - rank[b.severity])
      .slice(0, MAX_BANDS);
  }, [incidents, showIncidents, win]);

  return {
    meta,
    error,
    range,
    win,
    nowSec,
    maxSpan,
    panels,
    refreshing,
    updatedAt,
    refreshKey,
    annotations,
    showIncidents,
    changeRange,
    changeRefresh,
    refreshNow: useCallback(() => setTick((value) => value + 1), []),
    toggleIncidents: useCallback(() => setShowIncidents((value) => !value), []),
    onZoom,
    onZoomOut,
    onShift,
  };
};

export type ChartBoard = ReturnType<typeof useChartBoard>;
